import { Colors } from "../../../theme/Theme";

// Resolved brand accents for the RB Pay setup screen. Driven by the network
// branding (primaryColor / secondaryColor from RBContext) which differs per
// environment, with the static app theme as a fallback.
export interface RBPaySetupTheme {
  primary: string;
  secondary: string;
  secondarySoft: string;
  secondaryBorder: string;
  bannerFrom: string;
  bannerTo: string;
}

const THEME_BLUE_RGB = { r: 81, g: 52, b: 145 }; // Colors.themeBlue (#513491)

const hexToRgb = (color?: string | null): { r: number; g: number; b: number } | null => {
  if (!color) {
    return null;
  }

  const value = color.trim().replace("#", "");
  const normalized =
    value.length === 3
      ? value
          .split("")
          .map((char) => char + char)
          .join("")
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const clampChannel = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

export const hexToRgba = (color: string | null | undefined, alpha: number): string => {
  const rgb = hexToRgb(color) || THEME_BLUE_RGB;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

// percent < 0 darkens, > 0 lightens. Falls back to the original color string
// when it is not a parseable hex (e.g. already rgb()/named).
export const shadeColor = (color: string, percent: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return color;
  }

  const factor = percent / 100;
  const adjust = (channel: number) =>
    clampChannel(factor < 0 ? channel * (1 + factor) : channel + (255 - channel) * factor);

  return `rgb(${adjust(rgb.r)}, ${adjust(rgb.g)}, ${adjust(rgb.b)})`;
};

export const buildRBPaySetupTheme = (
  primaryColor?: string | null,
  secondaryColor?: string | null
): RBPaySetupTheme => {
  const primary = primaryColor || Colors.themeBlue;
  const secondary = secondaryColor || primaryColor || Colors.themeBlue;

  return {
    primary,
    secondary,
    secondarySoft: hexToRgba(secondary, 0.12),
    secondaryBorder: hexToRgba(secondary, 0.4),
    bannerFrom: shadeColor(primary, -16),
    bannerTo: primary,
  };
};
